import type { ChartDataPoint } from '@gonfalon/dataviz-experimental/types';
import type { queryChartData } from '@gonfalon/openapi/operations/queryChartData';

type ChartDataResponse = Awaited<ReturnType<typeof queryChartData>>;
export type TraceDataItem = NonNullable<NonNullable<ChartDataResponse['data']>['items']>[number];

export type ColumnIndices = {
  spanNameIdx: number;
  environmentIdx: number;
  timestampIdx: number;
  countIdx: number;
};

export type GroupedSpanEnv = { environment: string; sparklineData: ChartDataPoint[]; totalCount: number };
export type GroupedSpanRow = {
  spanName: string;
  environments: GroupedSpanEnv[];
  sparklineData: ChartDataPoint[];
  totalCount: number;
};

/**
 * Collapse the flat chart-data items into per-span groups, each carrying its
 * per-environment sparklines plus an aggregate sparkline/total across all
 * environments. Rows are sorted by descending total count, and so are the
 * environments within each row.
 */
export function groupTraceRows(items: readonly TraceDataItem[], indices: ColumnIndices): GroupedSpanRow[] {
  const { spanNameIdx, environmentIdx, timestampIdx, countIdx } = indices;

  type SpanGroup = { spanName: string; envs: Map<string, { sparklinePoints: Map<number, number> }> };
  const spans = new Map<string, SpanGroup>();

  for (const item of items) {
    const groupByValues = item.group_by_values ?? [];
    const spanName = String(groupByValues[spanNameIdx >= 0 ? spanNameIdx : 0] ?? '—');
    const env = environmentIdx >= 0 ? String(groupByValues[environmentIdx] ?? '—') : '—';

    let spanGroup = spans.get(spanName);
    if (!spanGroup) {
      spanGroup = { spanName, envs: new Map() };
      spans.set(spanName, spanGroup);
    }

    let envData = spanGroup.envs.get(env);
    if (!envData) {
      envData = { sparklinePoints: new Map() };
      spanGroup.envs.set(env, envData);
    }

    // Row cells may be null at runtime for sparse buckets even though the generated
    // FieldValue schema (string | number | boolean) doesn't model that yet.
    const rows = (item.data ?? []) as Array<Array<number | string | null>>;
    for (const row of rows) {
      const tsRaw = row[timestampIdx >= 0 ? timestampIdx : 0];
      const ts = typeof tsRaw === 'number' ? tsRaw : typeof tsRaw === 'string' ? new Date(tsRaw).getTime() : null;
      const countRaw = countIdx >= 0 ? row[countIdx] : null;
      const count =
        typeof countRaw === 'number'
          ? countRaw
          : typeof countRaw === 'string' && countRaw !== ''
            ? Number(countRaw)
            : 0;
      if (ts !== null && !Number.isNaN(ts) && !Number.isNaN(count)) {
        envData.sparklinePoints.set(ts, (envData.sparklinePoints.get(ts) ?? 0) + count);
      }
    }
  }

  return Array.from(spans.values())
    .map(({ spanName, envs }) => {
      const environments = Array.from(envs.entries())
        .map(([environment, { sparklinePoints }]) => {
          const sparklineData: ChartDataPoint[] = Array.from(sparklinePoints.entries())
            .sort(([a], [b]) => a - b)
            .map(([timestamp, count]) => ({ timestamp, count }));
          const totalCount = sparklineData.reduce((sum, d) => sum + (typeof d.count === 'number' ? d.count : 0), 0);
          return { environment, sparklineData, totalCount };
        })
        .sort((a, b) => b.totalCount - a.totalCount);

      const aggPoints = new Map<number, number>();
      for (const env of environments) {
        for (const p of env.sparklineData) {
          const ts = typeof p.timestamp === 'number' ? p.timestamp : null;
          const cnt = typeof p.count === 'number' ? p.count : 0;
          if (ts !== null) {
            aggPoints.set(ts, (aggPoints.get(ts) ?? 0) + cnt);
          }
        }
      }
      const sparklineData: ChartDataPoint[] = Array.from(aggPoints.entries())
        .sort(([a], [b]) => a - b)
        .map(([timestamp, count]) => ({ timestamp, count }));
      const totalCount = environments.reduce((sum, e) => sum + e.totalCount, 0);

      return { spanName, environments, sparklineData, totalCount };
    })
    .sort((a, b) => b.totalCount - a.totalCount);
}
